import type {
  TextBlock,
  PageDataMap,
  SubscriberAttributes,
  PageData,
  Block,
  PageDataSchema,
  ScheduledItem,
} from "./types";

import { BLOCK_TYPES, NODE_TYPES } from "./blocks";
import { FIELD_TYPES, getSlotFields, isSlotField, slotFieldSlug } from "./slot-fields";
import { isMultiLine, replaceTextInHtml } from "./text-utils";

export { FIELD_TYPES, slotFieldSlug, parseSlotFieldSlug, isSlotField } from "./slot-fields";

export const resolvePageDataSlug = ({
  sharesLayout = false,
  blockUuid,
  blockType,
  fieldKey,
  explicitSlug = null,
}: {
  sharesLayout?: boolean;
  blockUuid?: string | null;
  blockType?: string | null;
  fieldKey: string;
  explicitSlug?: string | null;
}): string | null => {
  if (explicitSlug) return explicitSlug;

  if (sharesLayout && blockUuid && blockType && isSlotField(blockType, fieldKey)) {
    return slotFieldSlug(blockUuid, fieldKey);
  }

  return null;
};

export const BLOCK_DATA_FIELD_KEYS = {
  IMAGE_PAGE_ATTRIBUTE: "image_page_attribute_slug",
  VIDEO_PAGE_ATTRIBUTE: "video_page_attribute_slug",
  AUDIO_PAGE_ATTRIBUTE: "audio_page_attribute_slug",
  PAGE_ATTRIBUTE: "page_attribute_slug",
} as const;

export const BUILT_IN_DATA_FIELDS = {
  TITLE: "page.title",
  SCHEDULED_DATE: "page.scheduled_date",
  FIRST_NAME: "member.first_name",
} as const;

export type BuiltInFieldContext = {
  pageTitle?: string | null;
  dataFallbacks?: Record<string, any>;
  currentSchedule?: ScheduledItem | null;
  locale?: string;
};

export const formatScheduledDate = (
  schedule: ScheduledItem | null | undefined,
  locale: string
): string | null => {
  if (!schedule) return null;

  if (schedule.is_all_day) {
    if (!schedule.starts_on) return null;
    const date = new Date(`${schedule.starts_on}T00:00:00Z`);
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
      timeZone: "UTC",
    }).format(date);
  }

  if (!schedule.starts_at) return null;
  const date = new Date(schedule.starts_at);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "short",
    hour12: true,
  }).format(date);
};

const BUILT_IN_DATA_FIELD_VALUES = Object.values(
  BUILT_IN_DATA_FIELDS
) as string[];

const PAGE_I18N_KEY_PREFIX = "pages";
const BLOCK_I18N_KEY_PREFIX = "blocks";

const pageI18nKey = (
  pageId: string | number | null | undefined,
  key: string
): string | null => {
  if (!pageId) return null;

  return `${PAGE_I18N_KEY_PREFIX}.${pageId}.${key}`;
};

const blockI18nKey = (
  blockUuid: string | null | undefined,
  key: string
): string | null => {
  if (!blockUuid) return null;

  return `${BLOCK_I18N_KEY_PREFIX}.${blockUuid}.${key}`;
};

const buildPageI18nKeys = (
  pageId: string | number | null | undefined,
  slug: string,
  fieldKey = `attributes.${slug}`
): Record<string, string> => {
  const valueKey = pageI18nKey(pageId, fieldKey);

  return valueKey ? { value: valueKey } : {};
};

const buildSlotI18nKeys = (
  blockUuid: string | null | undefined,
  fieldKey: string | null | undefined,
  type: string
): Record<string, string> => {
  if (
    !blockUuid ||
    !fieldKey ||
    (type !== FIELD_TYPES.STRING && type !== FIELD_TYPES.RICH_TEXT)
  ) {
    return {};
  }

  const valueKey = blockI18nKey(blockUuid, fieldKey);

  return valueKey ? { value: valueKey } : {};
};

const flattenBlocks = (blocks: Block[] = []): Block[] =>
  blocks.reduce<Block[]>((acc, block) => {
    acc.push(block);

    if (Array.isArray(block?.blocks) && block.blocks.length > 0) {
      acc.push(...flattenBlocks(block.blocks));
    }

    return acc;
  }, []);

const inferFieldValueFromBlock = (
  block: Block,
  fieldKey: string,
  type: string
): any => {
  if (!block?.data) return null;

  if (block.type === BLOCK_TYPES.TEXT_BLOCK && fieldKey === "content") {
    return parseToDataValue((block.data as TextBlock["data"]).content, {
      slug: slotFieldSlug(block.uuid, fieldKey),
      label: "Text",
      type,
      value: null,
    });
  }

  return (block.data as Record<string, any>)[fieldKey] ?? null;
};

const slotFieldLabel = (block: Block, slotLabel: string): string => {
  const blockLabel =
    block?.title ||
    block?.type
      ?.replace(/_block$/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  if (!blockLabel || blockLabel === slotLabel) return slotLabel;

  return `${blockLabel} - ${slotLabel}`;
};

export const hasPageDataOverride = (
  pageData: PageDataMap = {},
  slug: string | null | undefined
): boolean =>
  !!slug && Object.prototype.hasOwnProperty.call(pageData || {}, slug);

export const buildBuiltInFieldDefinitions = ({
  pageId = null,
  title = "",
  dataFallbacks = {},
}: {
  pageId?: string | number | null;
  title?: string | null;
  dataFallbacks?: Record<string, any>;
} = {}): PageDataMap => ({
  [BUILT_IN_DATA_FIELDS.TITLE]: {
    slug: BUILT_IN_DATA_FIELDS.TITLE,
    label: "Title",
    type: FIELD_TYPES.STRING,
    value: title || "",
    built_in: true,
    i18n_keys: buildPageI18nKeys(
      pageId,
      BUILT_IN_DATA_FIELDS.TITLE,
      "title"
    ),
  },
  [BUILT_IN_DATA_FIELDS.SCHEDULED_DATE]: {
    slug: BUILT_IN_DATA_FIELDS.SCHEDULED_DATE,
    label: "Scheduled Date",
    type: FIELD_TYPES.STRING,
    value: "",
    built_in: true,
  },
  [BUILT_IN_DATA_FIELDS.FIRST_NAME]: {
    slug: BUILT_IN_DATA_FIELDS.FIRST_NAME,
    label: "First Name",
    type: FIELD_TYPES.STRING,
    value: dataFallbacks?.[BUILT_IN_DATA_FIELDS.FIRST_NAME] ?? "",
    built_in: true,
    i18n_keys: buildPageI18nKeys(pageId, BUILT_IN_DATA_FIELDS.FIRST_NAME),
  },
});

export const buildSlotFieldDefinitionsFromBlocks = ({
  blocks = [],
}: {
  blocks?: Block[];
} = {}): PageDataMap =>
  flattenBlocks(blocks).reduce<PageDataMap>((acc, block) => {
    getSlotFields(block.type).forEach((field) => {
      const slug = slotFieldSlug(block.uuid, field.key);

      acc[slug] = {
        slug,
        label: slotFieldLabel(block, field.label),
        type: field.type,
        value: inferFieldValueFromBlock(block, field.key, field.type),
        slot_slug: slug,
        block_uuid: block.uuid,
        field_key: field.key,
        i18n_keys: buildSlotI18nKeys(block.uuid, field.key, field.type),
      };
    });

    return acc;
  }, {});

export const buildManualFieldDefinitions = ({
  pageId = null,
  dataSchema = {},
}: {
  pageId?: string | number | null;
  dataSchema?: PageDataSchema | null;
} = {}): PageDataMap =>
  (dataSchema?.manual_fields || []).reduce<PageDataMap>((acc, field) => {
    if (!field?.key) return acc;

    acc[field.key] = {
      slug: field.key,
      label: field.label || field.key,
      type: field.type || FIELD_TYPES.STRING,
      value: null,
      manual: true,
      i18n_keys: buildPageI18nKeys(pageId, field.key),
    };

    return acc;
  }, {});

export const applyDataSchemaOverlays = ({
  fields = {},
  dataSchema = {},
}: {
  fields?: PageDataMap;
  dataSchema?: PageDataSchema | null;
} = {}): PageDataMap => {
  const labels = dataSchema?.labels || {};
  const hidden = dataSchema?.hidden || [];
  const handles = dataSchema?.handles || {};
  const order = dataSchema?.order || [];
  const orderedSlugs = Object.keys(fields);

  orderedSlugs.forEach((slug, index) => {
    const field = fields[slug];
    const position = order.indexOf(slug);

    fields[slug] = {
      ...field,
      label: labels[slug] || field.label,
      hidden: hidden.includes(slug) || field.hidden,
      handle: handles[slug] || field.handle || null,
      position: position >= 0 ? position + 1 : order.length + index + 1,
    };
  });

  return fields;
};

const mergePersistedPageData = ({
  fields = {},
  persistedData = {},
}: {
  fields?: PageDataMap;
  persistedData?: PageDataMap;
} = {}): PageDataMap =>
  Object.keys(fields).reduce<PageDataMap>((acc, slug) => {
    const field = fields[slug];
    const persistedField = persistedData?.[slug];
    const hasPersistedOverride = hasPageDataOverride(persistedData, slug);
    const persistedFieldProps = persistedField ? { ...persistedField } : {};

    acc[slug] = {
      ...field,
      ...persistedFieldProps,
      slug: field.slug,
      label: field.label,
      type: field.type,
      slot_slug: field.slot_slug || persistedField?.slot_slug || null,
      block_uuid: field.block_uuid || persistedField?.block_uuid || null,
      field_key: field.field_key || persistedField?.field_key || null,
      i18n_keys: field.i18n_keys || persistedField?.i18n_keys || {},
      value: hasPersistedOverride ? persistedField?.value : field.value,
    };

    return acc;
  }, {});

export const selectableDataFields = ({
  dataSchema = {},
}: {
  dataSchema?: PageDataSchema | null;
}): PageDataMap =>
  ({
    ...buildBuiltInFieldDefinitions(),
    ...buildManualFieldDefinitions({ dataSchema }),
  });

export const materializePageFields = ({
  pageId = null,
  blocks = [],
  dataSchema = {},
  persistedData = {},
}: {
  pageId?: string | number | null;
  blocks?: Block[];
  dataSchema?: PageDataSchema | null;
  persistedData?: PageDataMap;
}): PageDataMap => {
  return mergePersistedPageData({
    fields: applyDataSchemaOverlays({
      fields: {
        ...buildSlotFieldDefinitionsFromBlocks({ blocks }),
        ...buildManualFieldDefinitions({ pageId, dataSchema }),
      },
      dataSchema,
    }),
    persistedData,
  });
};

export const findDataField = (
  pageData: PageDataMap = {},
  options: { slug?: string | null; slotSlug?: string | null },
  builtInFieldContext: BuiltInFieldContext = {}
): PageData | undefined => {
  if (hasPageDataOverride(pageData, options.slug)) {
    return pageData[options.slug as string];
  }

  if (hasPageDataOverride(pageData, options.slotSlug)) {
    return pageData[options.slotSlug as string];
  }

  if (options.slug) {
    if (options.slug === BUILT_IN_DATA_FIELDS.TITLE) {
      return {
        slug: BUILT_IN_DATA_FIELDS.TITLE,
        label: "Title",
        type: FIELD_TYPES.STRING,
        value: builtInFieldContext.pageTitle || "",
        built_in: true,
      };
    }

    if (options.slug === BUILT_IN_DATA_FIELDS.FIRST_NAME) {
      return {
        slug: BUILT_IN_DATA_FIELDS.FIRST_NAME,
        label: "First Name",
        type: FIELD_TYPES.STRING,
        value:
          builtInFieldContext.dataFallbacks?.[
            BUILT_IN_DATA_FIELDS.FIRST_NAME
          ] || "",
        built_in: true,
      };
    }

    if (options.slug === BUILT_IN_DATA_FIELDS.SCHEDULED_DATE) {
      const locale =
        builtInFieldContext.locale ||
        (typeof navigator !== "undefined" ? navigator.language : "en-US");
      const formatted = formatScheduledDate(
        builtInFieldContext.currentSchedule,
        locale
      );

      return {
        slug: BUILT_IN_DATA_FIELDS.SCHEDULED_DATE,
        label: "Scheduled Date",
        type: FIELD_TYPES.STRING,
        value: formatted || "",
        built_in: true,
      };
    }
  }

  if (!options.slotSlug) return undefined;

  return Object.values(pageData).find(
    (field) => field?.slot_slug === options.slotSlug
  );
};

export const highlight = (html: string, term: string) => {
  if (!term || !term.trim()) return html

  const container = document.createElement("div")
  container.innerHTML = html

  const keywords = term
    .trim()
    .split(/\s+/)
    .filter(k => k !== "")
    .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))

  if (keywords.length === 0) return html

  const regex = new RegExp(`(${keywords.join("|")})`, "gi")

  function walk(node: any) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? ""
      regex.lastIndex = 0
      if (!regex.test(text)) return

      const frag = document.createDocumentFragment()
      let lastIndex = 0
      regex.lastIndex = 0
      let match: RegExpExecArray | null
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
        }
        const mark = document.createElement("mark")
        mark.textContent = match[0]
        frag.appendChild(mark)
        lastIndex = match.index + match[0].length
        if (match[0].length === 0) regex.lastIndex++
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)))
      }
      node.replaceWith(frag)
    } else {
      node.childNodes.forEach(walk)
    }
  }

  walk(container)

  return container.innerHTML
}

export const getTextContent = (
  textBlock: TextBlock,
  pageData: PageDataMap = {},
  subscriberAttributes: SubscriberAttributes = {},
  builtInFieldContext: BuiltInFieldContext = {}
): string => {
  const { content, page_attribute_slug } = textBlock.data;
  const field = findDataField(
    pageData,
    {
      slug: page_attribute_slug,
      slotSlug: textBlock.uuid
        ? slotFieldSlug(textBlock.uuid, "content")
        : null,
    },
    builtInFieldContext
  );

  if (!field) {
    return resolveInlineDataFields(
      content,
      pageData,
      subscriberAttributes,
      builtInFieldContext
    );
  }

  const replacedValue = getDataFieldValue(field, subscriberAttributes);

  if (!replacedValue) return content;

  if (field.type === FIELD_TYPES.STRING) {
    return replaceTextInHtml(content, replacedValue);
  } else if (field.type === FIELD_TYPES.RICH_TEXT) {
    return replacedValue.startsWith("<")
      ? replacedValue
      : `<p>${replacedValue}</p>`;
  }

  return content;
};

export const resolveInlineDataFields = (
  content: string,
  pageData: PageDataMap,
  subscriberAttributes: SubscriberAttributes,
  builtInFieldContext: BuiltInFieldContext = {}
): string => {
  try {
    const body = stringToHtml(content);

    if (!body) return content;

    body
      .querySelectorAll(`[data-type=${NODE_TYPES.DATA_FIELD}]`)
      .forEach((e: any) => {
        e.innerHTML = getDataFieldValue(
          findDataField(
            pageData,
            { slug: e.dataset.slug, slotSlug: null },
            builtInFieldContext
          ),
          subscriberAttributes
        );
      });

    return body.innerHTML;
  } catch {
    return content;
  }
};

const stringToHtml = (content: string): Element | null => {
  try {
    return new DOMParser()
      .parseFromString(content, "text/html")
      .documentElement.querySelector("body");
  } catch {
    return null;
  }
};

export const getDataFieldValue = (
  dataField: PageData | null | undefined,
  subscriberAttributes: SubscriberAttributes
): string => {
  return dataField && isMemberField(dataField)
    ? subscriberAttributes[dataField.slug] || dataField.value
    : dataField?.value || "";
};

export const isMemberField = (
  dataField: PageData | string | null | undefined
): boolean =>
  dataField
    ? (typeof dataField === "object" && "slug" in dataField
        ? dataField.slug
        : dataField
      ).startsWith("member.")
    : false;

export const parseToDataValue = (
  text: string | null,
  dataField: PageData
): string => {
  if (!text) return "";

  if (dataField.type === FIELD_TYPES.STRING) {
    return (
      new DOMParser()
        .parseFromString(text, "text/html")
        .documentElement?.querySelector("body")?.childNodes[0]?.textContent ||
      ""
    );
  } else if (dataField.type === FIELD_TYPES.RICH_TEXT) {
    return text.startsWith("<") ? text : `<p>${text}</p>`;
  } else {
    return "";
  }
};

export const getDefaultFieldType = (block: Block) => {
  switch (block.type) {
    case BLOCK_TYPES.TEXT_BLOCK:
      return isMultiLine(block.data.content) ? FIELD_TYPES.RICH_TEXT : FIELD_TYPES.STRING;
    case BLOCK_TYPES.HERO_BLOCK:
      return FIELD_TYPES.IMAGE;
    default:
      return FIELD_TYPES.STRING;
  }
};

export const isContentOnlyChange = (
  blockType: string,
  changedKeys: string[]
): boolean => {
  if (changedKeys.length === 0) return false;

  return changedKeys.every((key) => isSlotField(blockType, key));
};

export const dataFieldIcon = (fieldType: string): string => {
  switch (fieldType) {
    case FIELD_TYPES.STRING:
      return "format_size";

    case FIELD_TYPES.RICH_TEXT:
      return "notes";

    case FIELD_TYPES.IMAGE:
      return "image";

    case FIELD_TYPES.VIDEO:
      return "slideshow";

    case FIELD_TYPES.AUDIO:
      return "headphones";

    default:
      return "";
  }
};
